import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { PatientAttachment } from '../../../core/models/patient-attachment.model';
import { PatientAttachmentsService } from '../../../core/services/patient-attachments.service';
import { NavigationService } from '../../../core/services/navigation.service';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { UserTimingService } from '../../../core/services/user-timing.service';
import { UserPresenceService } from 'app/core/services/user-presence.service';

import { ConsultNoteTitleComponent } from '../../components/consult-note-title/consult-note-title.component';
import { ConsultNoteHeaderComponent } from '../../components/consult-note-header/consult-note-header.component';
import { ValidatePatientPrescriptionsComponent } from '../../components/validate-patient-prescriptions/validate-patient-prescriptions.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-consult-note-prescriptions',
    templateUrl: './consult-note-prescriptions.component.html',
    styleUrls: ['./consult-note-prescriptions.component.scss'],
    imports: [ConsultNoteTitleComponent,
              ConsultNoteHeaderComponent,
              ValidatePatientPrescriptionsComponent,
              MatProgressSpinner]
})
export class ConsultNotePrescriptionsComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private patientAttachmentsService = inject(PatientAttachmentsService);
  private navigationService = inject(NavigationService);
  private titleService = inject(Title);
  private authService = inject(AuthenticationService);
  private userTimingService = inject(UserTimingService);
  private userPresenceService = inject(UserPresenceService);

  patientAttachmentId: string;
  patientAttachment: PatientAttachment;
  refreshing = true;

  constructor() {
    this.route.paramMap.subscribe(paramsMap => {
      this.handleRouteChange(paramsMap);
    });
  }

  ngOnInit() {
    this.patientAttachmentId = this.route.snapshot.paramMap.get('id');
    this.loadPatientAttachment(this.patientAttachmentId);
    this.userPresenceService.subscribeAndJoin("consultNote", this.patientAttachmentId)
  }

  ngOnDestroy() {
    this.stopTimeTracking();
    this.userPresenceService.leaveAndUnsubscribe("consultNote", this.patientAttachmentId)
  }

  private loadPatientAttachment(patientAttachmentId): void {
    this.refreshing = true;
    this.patientAttachmentsService.get(patientAttachmentId).subscribe(
      (patientAttachment: PatientAttachment) => {
        this.refreshing = false;
        this.patientAttachment = patientAttachment;
        this.startTimeTracking(this.patientAttachment);
        this.setTitle();
      },
      (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.navigationService.notFound();
        } else {
          console.error(err);
        }
      }
    );

    window.scrollTo(0, 0);
  }

  private setTitle() {
    const title = `R1 340B Recovery - ${this.patientAttachment.id} | Prescriptions`;
    this.titleService.setTitle(title);
  }

  private handleRouteChange(paramsMap) {
    const params = paramsMap['params'];
    const patientAttachmentId = params && params['id'];

    if (this.navigationService.shouldReloadPage(this.patientAttachment, patientAttachmentId)) {
      this.loadPatientAttachment(patientAttachmentId);
    }
  }

  private startTimeTracking(patientAttachment) {
    if (this.authService.isCaptureAdminUser) {
      this.userTimingService.track({
        page: 'consult_note_prescriptions',
        recordId: patientAttachment.id,
        status: patientAttachment.status,
      });
    }
  }

  private stopTimeTracking() {
    if (this.authService.isCaptureAdminUser) {
      this.userTimingService.stop();
    }
  }
}
